import React, { useState } from 'react';
import { ModelStory } from '../types';
import { ChevronDown, ChevronUp, Building2, AlertCircle, CheckCircle, Lightbulb } from 'lucide-react';
import TermTooltip from './TermTooltip';
import { getTermDefinition } from '../data/glossary-data';

interface StoryCardProps {
  story: ModelStory;
}

// Helper function to highlight terms in text with tooltips
const highlightTerms = (text: string): React.ReactNode => {
  // List of terms to highlight (in order of priority - longer phrases first)
  const termPatterns = [
    // Multi-word terms
    'Mixed-Integer Nonlinear Programming', 'Latin Hypercube Sampling', 'Monte Carlo simulation',
    'Dynamic Programming', 'transfer pricing', 'economic order quantity', 'price elasticity',
    'demand elasticity', 'working capital', 'inventory turnover', 'fill rate', 'safety stock',
    'differential evolution', 'Random Forest', 'Fourier analysis', 'gross margin',
    'effective tax rate', 'internal rate of return', 'net present value',
    'base erosion and profit shifting', 'carrying cost', 'linear programming',
    'mixed-integer programming', 'nonlinear programming', 'arm\'s length',
    // Acronyms and single words
    'MINLP', 'IPOPT', 'BEPS', 'ARIMA', 'EOQ', 'EBITDA', 'ETR', 'IRR', 'NPV', 'VaR', 'CVaR',
    'DPO', 'DSO', 'DIO', 'WACC', 'COGS', 'ROI', 'SKU', 'API', 'KPI', 'ML',
    'optimization', 'elasticity', 'solver', 'regression'
  ];

  // Create regex pattern to match terms (case-insensitive, word boundaries)
  const pattern = new RegExp(
    `\\b(${termPatterns.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`,
    'gi'
  );

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  // Track which terms we've already highlighted to avoid duplicates in close proximity
  const recentlyHighlighted = new Set<string>();
  const highlightCooldown = 100; // characters before we can highlight the same term again

  while ((match = pattern.exec(text)) !== null) {
    const matchedTerm = match[0];
    const termKey = matchedTerm.toLowerCase();

    // Check if we recently highlighted this term
    if (recentlyHighlighted.has(termKey)) {
      continue;
    }

    const definition = getTermDefinition(matchedTerm);

    if (definition) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${key++}`}>
            {text.substring(lastIndex, match.index)}
          </span>
        );
      }

      // Add highlighted term with tooltip
      parts.push(
        <TermTooltip key={`term-${key++}`} term={matchedTerm} definition={definition}>
          {matchedTerm}
        </TermTooltip>
      );

      // Mark this term as recently highlighted
      recentlyHighlighted.add(termKey);
      setTimeout(() => recentlyHighlighted.delete(termKey), highlightCooldown);

      lastIndex = match.index + matchedTerm.length;
    }
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(
      <span key={`text-${key++}`}>
        {text.substring(lastIndex)}
      </span>
    );
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

const StoryCard: React.FC<StoryCardProps> = ({ story }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="story-section">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-semibold text-purple-700">{story.industry}</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{story.companyName}</h3>
          <p className="text-gray-700 leading-relaxed">{story.context}</p>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="ml-4 p-2 hover:bg-purple-100 rounded-lg transition-colors"
        >
          {expanded ? (
            <ChevronUp className="w-6 h-6 text-purple-600" />
          ) : (
            <ChevronDown className="w-6 h-6 text-purple-600" />
          )}
        </button>
      </div>

      {expanded && (
        <div className="space-y-6 mt-6 pt-6 border-t border-purple-200">
          {/* Problem */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <h4 className="text-lg font-bold text-gray-900">The Problem</h4>
            </div>
            <p className="text-gray-700 leading-relaxed mb-3">{highlightTerms(story.problem)}</p>
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <p className="text-sm font-semibold text-red-900 mb-1">Why This Matters</p>
              <p className="text-sm text-red-800">{highlightTerms(story.problemImportance)}</p>
            </div>
          </div>

          {/* Solution */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              <h4 className="text-lg font-bold text-gray-900">The Solution</h4>
            </div>
            <p className="text-gray-700 leading-relaxed mb-3">{highlightTerms(story.solution)}</p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <p className="text-sm font-semibold text-blue-900 mb-1">Why This Approach</p>
              <p className="text-sm text-blue-800">{highlightTerms(story.approachRationale)}</p>
            </div>
          </div>

          {/* Impact */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h4 className="text-lg font-bold text-gray-900">The Impact</h4>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">{highlightTerms(story.solutionImpact)}</p>
            
            {/* Metrics Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-red-50 rounded-lg p-4">
                <h5 className="font-bold text-red-900 mb-3">Before</h5>
                <div className="space-y-2">
                  {Object.entries(story.metrics.before).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="text-red-700">{key}:</span>
                      <span className="font-semibold text-red-900">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <h5 className="font-bold text-green-900 mb-3">After</h5>
                <div className="space-y-2">
                  {Object.entries(story.metrics.after).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="text-green-700">{key}:</span>
                      <span className="font-semibold text-green-900">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!expanded && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setExpanded(true)}
            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            Read full case study →
          </button>
        </div>
      )}
    </div>
  );
};

export default StoryCard;
